import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  DEFAULT_RIBBON_STYLE,
  DynamicRibbonFormat,
  IRibbonColors,
  IRibbonStyle,
} from "@/api_framework/api_modals/HomeTileConfig";
import { useHomeTileListAPI } from "@/app/hooks/api_hooks/quick_commerce/useHomeTileListAPI";
import { RibbonPreview } from "../components";
import { RibbonTextSuggestions } from "../controls";
import {
  DYNAMIC_RIBBON_EXAMPLES,
  RIBBON_POSITION_CLASSNAME,
  RIBBON_TEXT_SUGGESTIONS,
  sectionLabelClassName,
} from "../interface";

const MEMBERSHIP_PREVIEW_STYLE =
  RIBBON_TEXT_SUGGESTIONS.find((suggestion) => suggestion.text === "12% Off") ?? DEFAULT_RIBBON_STYLE;

export const DynamicRibbonStyleModal = ({
  isOpen,
  tileId,
  tileName,
  isDynamicEnabled,
  currentRibbon,
  currentFormat,
  onClose,
  onSaved,
}: {
  isOpen: boolean;
  tileId: string;
  tileName: string;
  isDynamicEnabled: boolean;
  currentRibbon: IRibbonStyle | null;
  currentFormat: DynamicRibbonFormat | null;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const { isUpdatingDynamicStatus, updateDynamicStatus } = useHomeTileListAPI();
  const [selectedStyle, setSelectedStyle] = useState<IRibbonColors | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<DynamicRibbonFormat | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedStyle(
      currentRibbon
        ? {
            bg_color: currentRibbon.bg_color,
            text_color: currentRibbon.text_color,
            border_color: currentRibbon.border_color,
          }
        : null,
    );
    setSelectedFormat(currentFormat);
  }, [isOpen, currentRibbon, currentFormat]);

  const handleClose = () => {
    if (!isUpdatingDynamicStatus) onClose();
  };

  const handleConfirm = async () => {
    if (!selectedStyle || !selectedFormat) return;
    const ribbonStyle: IRibbonStyle = {
      ...selectedStyle,
      position: currentRibbon?.position ?? DEFAULT_RIBBON_STYLE.position,
    };
    const isUpdated = await updateDynamicStatus(tileId, isDynamicEnabled, ribbonStyle, selectedFormat);
    if (isUpdated) {
      onSaved();
      onClose();
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-40" initialFocus={cancelButtonRef} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <Dialog.Title as="h3" className="text-base font-semibold text-gray-900">
                      {`Edit Dynamic Ribbon for ${tileName}`}
                    </Dialog.Title>
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-xs leading-5 text-slate-500">
                      <span className={sectionLabelClassName}>Select the ribbon format for membership</span>
                      <span role="radiogroup" aria-label="Ribbon format" className="mt-2 grid grid-cols-3 gap-2">
                        {DYNAMIC_RIBBON_EXAMPLES.map((example) => {
                          const isFormatSelected = selectedFormat === example.format;
                          return (
                            <button
                              key={example.format}
                              type="button"
                              role="radio"
                              aria-checked={isFormatSelected}
                              disabled={isUpdatingDynamicStatus}
                              onClick={() => setSelectedFormat(example.format)}
                              className={`flex flex-col items-center gap-2 rounded-lg border px-2 pb-3 pt-5 text-center transition-colors disabled:cursor-not-allowed ${
                                isFormatSelected
                                  ? "border-[#193A6A] bg-[#EEF5FF] ring-1 ring-[#193A6A]"
                                  : "border-slate-200 bg-white hover:border-[#193A6A]/40"
                              }`}
                            >
                              <span className="relative flex h-14 w-full items-end justify-center rounded-lg border border-slate-200 bg-white pb-1.5">
                                <span className={`absolute ${RIBBON_POSITION_CLASSNAME[DEFAULT_RIBBON_STYLE.position]}`}>
                                  <RibbonPreview text={example.text} style={MEMBERSHIP_PREVIEW_STYLE} />
                                </span>
                                <span className="max-w-full truncate px-1 text-[10px] font-medium text-slate-400">
                                  {tileName}
                                </span>
                              </span>
                              <span
                                className={`text-[10px] leading-4 ${
                                  isFormatSelected ? "font-semibold text-[#193A6A]" : "text-slate-500"
                                }`}
                              >
                                {example.label}
                              </span>
                            </button>
                          );
                        })}
                      </span>
                      <div className="mt-4">
                      <RibbonTextSuggestions
                        disabled={isUpdatingDynamicStatus}
                        selectedStyle={selectedStyle ?? undefined}
                        onSelect={(suggestion) =>
                          setSelectedStyle({
                            bg_color: suggestion.bg_color,
                            text_color: suggestion.text_color,
                            border_color: suggestion.border_color,
                          })
                        }
                        showEmojiPicker={false}
                      />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-slate-100 pt-4">
                  <button
                    ref={cancelButtonRef}
                    type="button"
                    onClick={handleClose}
                    disabled={isUpdatingDynamicStatus}
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={isUpdatingDynamicStatus || !selectedStyle || !selectedFormat}
                    className="rounded-lg bg-[#193A6A] px-4 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#132c52] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isUpdatingDynamicStatus ? "Saving…" : "Save"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
